import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Search, ShieldAlert, AlertTriangle, Clock, MoreHorizontal } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { apiClient } from '@/lib/api-client'
import { useToast } from '@/hooks/use-toast'

interface CveEntry {
  id: string
  cveId: string
  title: string
  description: string | null
  severity: string
  cvssScore: number | null
  isKev: boolean
  vendor: string | null
  product: string | null
  status: string
  publishedDate: string
  createdAt: string
}

function severityBadge(severity: string) {
  switch (severity) {
    case 'critical': return <Badge variant="destructive">Critical</Badge>
    case 'high': return <Badge className="bg-orange-500/10 text-orange-500 border-orange-500/20">High</Badge>
    case 'medium': return <Badge className="bg-yellow-500/10 text-yellow-500 border-yellow-500/20">Medium</Badge>
    case 'low': return <Badge className="bg-green-500/10 text-green-500 border-green-500/20">Low</Badge>
    default: return <Badge variant="secondary">{severity}</Badge>
  }
}

function cveStatusBadge(status: string) {
  switch (status) {
    case 'new': return <Badge variant="secondary">New</Badge>
    case 'reviewing': return <Badge variant="info">Reviewing</Badge>
    case 'mitigated': return <Badge variant="warning">Mitigated</Badge>
    case 'resolved': return <Badge variant="success">Resolved</Badge>
    case 'ignored': return <Badge variant="outline">Ignored</Badge>
    default: return <Badge variant="secondary">{status}</Badge>
  }
}

function AddCveDialog() {
  const [open, setOpen] = useState(false)
  const [cveId, setCveId] = useState('')
  const [title, setTitle] = useState('')
  const [severity, setSeverity] = useState('medium')
  const [vendor, setVendor] = useState('')
  const [product, setProduct] = useState('')
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const createMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => apiClient.post('/cve-feed', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cve-feed'] })
      toast({ title: 'CVE added', description: 'CVE entry created successfully' })
      setOpen(false)
      setCveId('')
      setTitle('')
      setVendor('')
      setProduct('')
    },
    onError: () => toast({ title: 'Error', description: 'Failed to add CVE', variant: 'destructive' }),
  })

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button><Plus className="mr-2 h-4 w-4" /> Add CVE</Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Add CVE Entry</DialogTitle>
          <DialogDescription>Manually add a CVE to track.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>CVE ID</Label>
            <Input value={cveId} onChange={(e) => setCveId(e.target.value)} placeholder="CVE-2026-12345" />
          </div>
          <div className="space-y-2">
            <Label>Title</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Remote code execution in..." />
          </div>
          <div className="space-y-2">
            <Label>Severity</Label>
            <Select value={severity} onValueChange={setSeverity}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="low">Low</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Vendor</Label>
              <Input value={vendor} onChange={(e) => setVendor(e.target.value)} placeholder="Microsoft" />
            </div>
            <div className="space-y-2">
              <Label>Product</Label>
              <Input value={product} onChange={(e) => setProduct(e.target.value)} placeholder="Exchange Server" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            onClick={() => createMutation.mutate({
              cveId,
              title,
              severity,
              vendor: vendor || undefined,
              product: product || undefined,
            })}
            disabled={!cveId || !title || createMutation.isPending}
          >
            {createMutation.isPending ? 'Adding...' : 'Add CVE'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export function CveFeedPage() {
  const [search, setSearch] = useState('')
  const [severityFilter, setSeverityFilter] = useState<string>('all')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [kevOnly, setKevOnly] = useState(false)
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const { data, isLoading } = useQuery({
    queryKey: ['cve-feed', severityFilter, statusFilter, kevOnly],
    queryFn: () => apiClient.get<{ cves: CveEntry[]; pagination: unknown }>('/cve-feed', {
      ...(severityFilter !== 'all' && { severity: severityFilter }),
      ...(statusFilter !== 'all' && { status: statusFilter }),
      ...(kevOnly && { kevOnly: true }),
    }),
  })

  const statusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      apiClient.patch(`/cve-feed/${id}`, { status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cve-feed'] })
      toast({ title: 'Updated', description: 'CVE status updated' })
    },
    onError: () => toast({ title: 'Error', description: 'Failed to update status', variant: 'destructive' }),
  })

  const cves = data?.data?.cves || []
  const filtered = cves.filter((c) =>
    c.cveId.toLowerCase().includes(search.toLowerCase()) ||
    c.title.toLowerCase().includes(search.toLowerCase())
  )

  const criticalCount = cves.filter((c) => c.severity === 'critical').length
  const kevCount = cves.filter((c) => c.isKev).length
  const unresolvedCount = cves.filter((c) => c.status !== 'resolved' && c.status !== 'ignored').length

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">CVE / ISM Feed</h1>
          <p className="text-muted-foreground">Track vulnerabilities, CISA KEV entries, and ISM controls</p>
        </div>
        <AddCveDialog />
      </div>

      <div className="grid gap-4 md:grid-cols-4">
        <Card><CardContent className="pt-6"><div className="flex items-center gap-2"><ShieldAlert className="h-4 w-4 text-muted-foreground" /><p className="text-xs text-muted-foreground">Total CVEs</p></div><div className="text-2xl font-bold mt-1">{cves.length}</div></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-red-500" /><p className="text-xs text-muted-foreground">Critical</p></div><div className="text-2xl font-bold text-red-500 mt-1">{criticalCount}</div></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="flex items-center gap-2"><ShieldAlert className="h-4 w-4 text-orange-500" /><p className="text-xs text-muted-foreground">CISA KEV</p></div><div className="text-2xl font-bold text-orange-500 mt-1">{kevCount}</div></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="flex items-center gap-2"><Clock className="h-4 w-4 text-yellow-500" /><p className="text-xs text-muted-foreground">Unresolved</p></div><div className="text-2xl font-bold text-yellow-500 mt-1">{unresolvedCount}</div></CardContent></Card>
      </div>

      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Search CVEs..." className="pl-9" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <Select value={severityFilter} onValueChange={setSeverityFilter}>
          <SelectTrigger className="w-[140px]"><SelectValue placeholder="Severity" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Severity</SelectItem>
            <SelectItem value="critical">Critical</SelectItem>
            <SelectItem value="high">High</SelectItem>
            <SelectItem value="medium">Medium</SelectItem>
            <SelectItem value="low">Low</SelectItem>
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[140px]"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="new">New</SelectItem>
            <SelectItem value="reviewing">Reviewing</SelectItem>
            <SelectItem value="mitigated">Mitigated</SelectItem>
            <SelectItem value="resolved">Resolved</SelectItem>
            <SelectItem value="ignored">Ignored</SelectItem>
          </SelectContent>
        </Select>
        <Button
          variant={kevOnly ? 'default' : 'outline'}
          size="sm"
          onClick={() => setKevOnly(!kevOnly)}
        >
          KEV Only
        </Button>
      </div>

      {isLoading ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[...Array(6)].map((_, i) => <Card key={i}><CardHeader><Skeleton className="h-5 w-40" /></CardHeader><CardContent><Skeleton className="h-4 w-full" /><Skeleton className="h-4 w-2/3 mt-2" /></CardContent></Card>)}
        </div>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <ShieldAlert className="h-12 w-12 mb-4 opacity-50 text-muted-foreground" />
            <p className="text-lg font-medium text-muted-foreground">No CVEs found</p>
            <p className="text-sm text-muted-foreground">Add a CVE or adjust your filters</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filtered.map((cve) => (
            <Card key={cve.id}>
              <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-3">
                <div className="space-y-1">
                  <CardTitle className="text-base font-bold">{cve.cveId}</CardTitle>
                  <p className="text-sm text-muted-foreground line-clamp-2">{cve.title}</p>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon"><MoreHorizontal className="h-4 w-4" /></Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => statusMutation.mutate({ id: cve.id, status: 'reviewing' })}>Mark as Reviewing</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => statusMutation.mutate({ id: cve.id, status: 'mitigated' })}>Mark as Mitigated</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => statusMutation.mutate({ id: cve.id, status: 'resolved' })}>Mark as Resolved</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => statusMutation.mutate({ id: cve.id, status: 'ignored' })}>Mark as Ignored</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2 mb-3">
                  {severityBadge(cve.severity)}
                  {cve.cvssScore !== null && (
                    <Badge variant="outline">CVSS {cve.cvssScore.toFixed(1)}</Badge>
                  )}
                  {cve.isKev && (
                    <Badge className="bg-orange-500/10 text-orange-500 border-orange-500/20">KEV</Badge>
                  )}
                  {cveStatusBadge(cve.status)}
                </div>
                <div className="text-sm text-muted-foreground space-y-1">
                  {(cve.vendor || cve.product) && (
                    <p>{[cve.vendor, cve.product].filter(Boolean).join(' / ')}</p>
                  )}
                  <p className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    Published: {new Date(cve.publishedDate).toLocaleDateString()}
                  </p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}
